import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, errText } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF8F5]">
      {/* Minimal, SaaS-first brand panel — CSS/SVG only, no stock photography */}
      <div className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-[#1B3B2B] text-[#FAF8F5] p-12">
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 32% 30%, rgba(201,162,39,0.20), transparent 55%)" }} />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(#FAF8F5 1px, transparent 1px), linear-gradient(90deg, #FAF8F5 1px, transparent 1px)", backgroundSize: "48px 48px" }} />

        <div className="relative flex items-center gap-2.5">
          <img src="/icon-192.png" alt="Equiro" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-display font-extrabold text-2xl tracking-tight">Equiro</span>
        </div>

        <div className="relative flex-1 flex items-center justify-center">
          <div className="relative">
            {/* discreet concentric equestrian rings */}
            <svg viewBox="0 0 320 320" className="w-[300px] h-[300px]" fill="none" aria-hidden="true">
              <circle cx="160" cy="160" r="150" stroke="#C9A227" strokeOpacity="0.18" strokeWidth="1" />
              <circle cx="160" cy="160" r="118" stroke="#C9A227" strokeOpacity="0.28" strokeWidth="1" />
              <path d="M40 160 A120 120 0 0 1 280 160" stroke="#C9A227" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <img src="/icon-512.png" alt="Equiro"
              className="absolute inset-0 m-auto h-32 w-32 rounded-3xl object-cover shadow-2xl ring-1 ring-[#C9A227]/30" />
          </div>
        </div>

        <div className="relative">
          <h2 className="font-display text-3xl font-bold leading-tight">La tua scuderia, organizzata.</h2>
          <p className="mt-3 text-[#CBD8CF] max-w-md text-sm leading-relaxed">
            Il gestionale professionale per scuderie: anagrafica, scadenze sanitarie, mascalcia,
            terapie e calendario lezioni in un'unica piattaforma.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 pt-safe pb-safe px-safe">
        <div className="w-full max-w-sm eq-fade-up">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/icon-192.png" alt="Equiro" className="h-11 w-11 rounded-xl object-cover" />
            <span className="font-display font-extrabold text-2xl text-[#1B3B2B]">Equiro</span>
          </div>
          <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Bentornato</h1>
          <p className="text-[#5F6B64] mt-1 mb-8">Accedi al tuo account per continuare.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-[#1B3B2B] hover:bg-[#142E1F] h-11">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accedi
            </Button>
          </form>
          <p className="text-sm text-[#5F6B64] mt-6 text-center">
            Non hai un account?{" "}
            <Link to="/register" className="text-[#8C532B] font-semibold" data-testid="go-register">
              Registrati
            </Link>
          </p>
          <p className="text-sm text-[#5F6B64] mt-2 text-center">
            <Link to="/password-dimenticata" className="text-[#8C532B] font-semibold" data-testid="go-forgot-password">
              Password dimenticata?
            </Link>
          </p>
          <p className="text-sm text-[#5F6B64] mt-6 text-center" data-testid="login-legal-links">
            <Link to="/privacy" className="text-[#8C532B]">Privacy</Link>{" · "}
            <Link to="/termini" className="text-[#8C532B]">Termini</Link>
          </p>
        </div>
      </div>
    </div>
  );
}